"use client";

import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { format } from "date-fns";

import NativeCalendar from "./NativeCalendar";
import {
  Field,
  INPUT_CLASSNAME,
  PRIMARY_BUTTON_CLASSNAME,
  TextField,
} from "./primitives";

/**
 * Step 1 of the Apply Now flow — ported from the source's `StudentInfoForm`.
 *
 * ── Why the validation is not zod ───────────────────────────────────────────
 * The source validates with `zodResolver(studentInfoSchema)`. This project is on
 * zod 4 while the installed `@hookform/resolvers` is 3.x, whose `zodResolver`
 * targets zod 3 — pairing them is a runtime hazard. The rules below are a
 * one-for-one transcription of that schema, including its exact messages, using
 * react-hook-form's built-in validation so no resolver is involved.
 *
 * The source's `mode: "onChange"` is kept, so errors clear as the student types.
 */

export interface StudentInfoFormValues {
  fullName: string;
  email: string;
  dob?: Date;
  mobileNumber: string;
  fatherName: string;
  motherName: string;
  universityName: string;
  collegeName: string;
  programName: string;
  studyYear: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  abcId: string;
  aadhaarId: string;
}

interface StudentInfoFormProps {
  onSubmit: (data: StudentInfoFormValues) => void;
  defaultValues?: Partial<StudentInfoFormValues>;
}

/** Ported verbatim from the source. */
const INDIAN_STATES = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

const EMPTY_VALUES: StudentInfoFormValues = {
  fullName: "",
  email: "",
  dob: undefined,
  mobileNumber: "",
  fatherName: "",
  motherName: "",
  universityName: "",
  collegeName: "",
  programName: "",
  studyYear: "",
  address: "",
  city: "",
  state: "",
  pincode: "",
  abcId: "",
  aadhaarId: "",
};

/** `z.string().min(n, …)` — the source trims nothing, so neither does this. */
const minLength = (n: number, message: string) => ({
  required: message,
  minLength: { value: n, message },
});

/** The source's `SelectTrigger`, which shares the Input's chrome. */
const SELECT_CLASSNAME = `${INPUT_CLASSNAME} items-center justify-between py-2`;

export default function StudentInfoForm({
  onSubmit,
  defaultValues,
}: StudentInfoFormProps) {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<StudentInfoFormValues>({
    defaultValues: { ...EMPTY_VALUES, ...defaultValues },
    mode: "onChange",
  });

  const [isDobOpen, setIsDobOpen] = useState(false);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 tab:grid-cols-2 gap-4">
        <Field label="Your Name" error={errors.fullName?.message}>
          <TextField
            placeholder="Enter your full name"
            // The source leaves this stray `bg-blue-50` on the first field only.
            className="bg-blue-50"
            {...register(
              "fullName",
              minLength(3, "Name must be at least 3 characters")
            )}
          />
        </Field>

        <Field label="Email Address" error={errors.email?.message}>
          <TextField
            type="email"
            placeholder="Enter your email address"
            {...register("email", {
              required: "Please enter a valid email address",
              pattern: {
                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                message: "Please enter a valid email address",
              },
            })}
          />
        </Field>

        {/* Date of birth — the source uses a Popover + the hand-rolled calendar. */}
        <Controller
          control={control}
          name="dob"
          rules={{ required: "Date of birth is required" }}
          render={({ field }) => (
            <Field
              label="Date of Birth"
              error={errors.dob?.message}
              className="flex flex-col"
            >
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setIsDobOpen((open) => !open)}
                  className={`w-full pl-3 text-left font-normal h-[36px] border border-[#bebebe] rounded-md text-sm bg-transparent shadow-xs ${
                    field.value ? "" : "text-[#BEBEBE]"
                  }`}
                >
                  {field.value
                    ? format(field.value, "dd/MM/yyyy")
                    : "Select your date of birth"}
                </button>

                {isDobOpen ? (
                  <>
                    {/* Click-away layer; the source gets this from Popover. */}
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setIsDobOpen(false)}
                      aria-hidden="true"
                    />
                    <div className="absolute left-0 top-full z-50 mt-1">
                      <NativeCalendar
                        selected={field.value}
                        onSelect={(date) => {
                          field.onChange(date);
                          setIsDobOpen(false);
                        }}
                        disabled={(date) =>
                          date > new Date() || date < new Date("1940-01-01")
                        }
                      />
                    </div>
                  </>
                ) : null}
              </div>
            </Field>
          )}
        />

        <Field label="Phone Number" error={errors.mobileNumber?.message}>
          <TextField
            type="tel"
            inputMode="numeric"
            maxLength={10}
            placeholder="Enter your 10-digit mobile number"
            {...register("mobileNumber", {
              required: "Please enter a valid 10-digit mobile number",
              pattern: {
                value: /^[6-9]\d{9}$/,
                message: "Please enter a valid 10-digit mobile number",
              },
            })}
          />
        </Field>

        <Field label="Father's Name" error={errors.fatherName?.message}>
          <TextField
            placeholder="Enter your father's name"
            {...register(
              "fatherName",
              minLength(3, "Father's name must be at least 3 characters")
            )}
          />
        </Field>

        <Field label="Mother's Name" error={errors.motherName?.message}>
          <TextField
            placeholder="Enter your mother's name"
            {...register(
              "motherName",
              minLength(3, "Mother's name must be at least 3 characters")
            )}
          />
        </Field>

        <Field label="Current University" error={errors.universityName?.message}>
          <TextField
            placeholder="Enter your university name"
            {...register(
              "universityName",
              minLength(3, "University name must be at least 3 characters")
            )}
          />
        </Field>

        <Field label="Current College" error={errors.collegeName?.message}>
          <TextField
            placeholder="Enter your college name"
            {...register(
              "collegeName",
              minLength(3, "College name must be at least 3 characters")
            )}
          />
        </Field>

        {/* The source pairs this label with an info popover reading "e.g. BTech,
            BBA". A `title` tooltip carries the same hint without a popover. */}
        <Field
          label={
            <span className="flex items-center gap-1">
              Current Program
              <span
                title="e.g. BTech, BBA"
                className="cursor-help text-[#737373]"
                aria-label="e.g. BTech, BBA"
              >
                {/* lucide Info, inlined */}
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 16v-4M12 8h.01" />
                </svg>
              </span>
            </span>
          }
          error={errors.programName?.message}
        >
          <TextField
            placeholder="Enter your program name"
            {...register(
              "programName",
              minLength(3, "Program name must be at least 3 characters")
            )}
          />
        </Field>

        <Field label="Year of Study" error={errors.studyYear?.message}>
          <TextField
            placeholder="e.g. First Year"
            {...register("studyYear", {
              required: "Year of study is required",
            })}
          />
        </Field>

        <Field label="Address" error={errors.address?.message}>
          <TextField
            placeholder="Enter your address"
            {...register(
              "address",
              minLength(5, "Address must be at least 5 characters")
            )}
          />
        </Field>

        <Field label="City" error={errors.city?.message}>
          <TextField
            placeholder="Enter your city"
            {...register("city", minLength(2, "City must be at least 2 characters"))}
          />
        </Field>

        <Field label="State" error={errors.state?.message}>
          <select
            className={SELECT_CLASSNAME}
            defaultValue=""
            {...register("state", { required: "Please select a state" })}
          >
            <option value="" disabled>
              Select a state
            </option>
            {INDIAN_STATES.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Pincode" error={errors.pincode?.message}>
          <TextField
            inputMode="numeric"
            maxLength={6}
            placeholder="Enter 6-digit pincode"
            {...register("pincode", {
              required: "Please enter a valid 6-digit pincode",
              pattern: {
                value: /^\d{6}$/,
                message: "Please enter a valid 6-digit pincode",
              },
            })}
          />
        </Field>

        <Field label="ABC ID" error={errors.abcId?.message}>
          <TextField
            maxLength={12}
            placeholder="Enter your 12-character ABC ID"
            {...register("abcId", {
              required: "ABC ID must be exactly 12 characters",
              minLength: {
                value: 12,
                message: "ABC ID must be exactly 12 characters",
              },
              maxLength: {
                value: 12,
                message: "ABC ID must be exactly 12 characters",
              },
            })}
          />
        </Field>

        <Field label="Aadhaar ID" error={errors.aadhaarId?.message}>
          <TextField
            inputMode="numeric"
            maxLength={12}
            placeholder="Enter 12-digit Aadhaar number"
            {...register("aadhaarId", {
              required: "Please enter a valid 12-digit Aadhaar number",
              pattern: {
                value: /^\d{12}$/,
                message: "Please enter a valid 12-digit Aadhaar number",
              },
            })}
          />
        </Field>
      </div>

      <div className="flex justify-center mt-6">
        <button
          type="submit"
          className={`${PRIMARY_BUTTON_CLASSNAME} text-sm min-[1024px]:text-lg`}
        >
          Next
        </button>
      </div>
    </form>
  );
}
